'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronRight, Clock, Heart, MapPin } from 'lucide-react';
import type { CareOffer, CareRef, CareType, LocationGroup } from '@/lib/types';
import { campusSvgMap } from '@/lib/types';
import { fetchCare, fetchCareCatalogue, fetchCollection, fetchLocations } from '@/lib/api';
import { fmtDay, parseMp } from '@/lib/format';
import { toPlainText } from '@/lib/richText';
import { useEventChrome } from '@/lib/useEventChrome';
import { CampusSvgProvider } from './CampusIcon';
import { CarePanel } from './CarePanel';
import { PageSkeleton } from './OpportunityPage';
import { serveHref } from './ServeCard';
import { BandArt, PageBand, accentTokens, isHexColor } from './PageBand';

/**
 * /care, /care/{programme}, /care/{programme}/{campus}: the asking side on its
 * own, for the people the church is caring for. Flyers handed to families
 * print these addresses; the /serve Collection links here and back.
 *
 *   /care                     every public offer
 *   /care/thanksgiving        one programme (a public Care Type) and its offers
 *   /care/thanksgiving/troy   one offer, picked, with its siblings around it
 */

type Status = 'loading' | 'ready' | 'missing' | 'error';

export function CarePage({ apiBaseUrl, reference }: { apiBaseUrl: string; reference: string }) {
  const [found, setFound] = useState<
    CareRef | { kind: 'all'; types: CareType[]; offers: CareOffer[] } | null
  >(null);
  const [status, setStatus] = useState<Status>('loading');
  const [locations, setLocations] = useState<LocationGroup[]>([]);
  const [helpUrl, setHelpUrl] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    const load = reference
      ? fetchCare(apiBaseUrl, reference, controller.signal)
      : fetchCareCatalogue(apiBaseUrl, controller.signal).then((c) => ({
          kind: 'all' as const,
          ...c,
        }));
    load
      .then((r) => {
        if (controller.signal.aborted) return;
        setFound(r);
        setStatus(r ? 'ready' : 'missing');
      })
      .catch(() => !controller.signal.aborted && setStatus('error'));
    fetchLocations(apiBaseUrl, controller.signal)
      .then(setLocations)
      .catch(() => {});
    return () => controller.abort();
  }, [apiBaseUrl, reference]);

  const offers = useMemo<CareOffer[]>(() => {
    if (!found) return [];
    if (found.kind === 'offer') return [found.offer, ...found.siblings];
    return found.offers;
  }, [found]);
  const type = found?.kind === 'type' ? found.type : found?.kind === 'offer' ? found.type : null;

  // The serving side, for the "help instead" link: the Collection's own page.
  const collectionId = offers.find((o) => o.collectionId)?.collectionId ?? null;
  useEffect(() => {
    if (!collectionId) return;
    const controller = new AbortController();
    fetchCollection(apiBaseUrl, collectionId, controller.signal)
      .then((d) => {
        if (!d || controller.signal.aborted) return;
        const origin = (() => {
          try {
            return new URL(d.links.details).origin;
          } catch {
            return '';
          }
        })();
        setHelpUrl(
          d.collection.slug
            ? `${origin}/serve/${d.collection.slug}/`
            : serveHref(d.links.details, d.collection.id)
        );
      })
      .catch(() => {});
    return () => controller.abort();
  }, [apiBaseUrl, collectionId]);

  const svgMap = useMemo(() => campusSvgMap(locations), [locations]);
  const rootRef = useRef<HTMLDivElement>(null);
  useEventChrome(rootRef, type?.accentColor, type?.accentInk, status);

  if (status !== 'ready' || !found) {
    return status === 'loading' ? (
      <PageSkeleton kind="care" />
    ) : (
      <div className="flex flex-col items-center gap-3 rounded-2xl border border-gray-200 bg-white px-6 py-14 text-center">
        <p className="text-lg font-bold text-[#1c2b39]">
          {status === 'missing' ? "We couldn't find that." : "We couldn't load this right now."}
        </p>
      </div>
    );
  }

  const accent =
    type && isHexColor(type.accentColor) ? accentTokens(type.accentColor, type.accentInk) : null;
  const vars = accent
    ? ({
        '--accent': accent.base,
        '--on-accent': accent.onAccent,
        '--accent-text': accent.text,
        '--accent-deep': accent.deep,
        '--accent-soft': accent.soft,
      } as React.CSSProperties)
    : undefined;

  const dates = offers
    .map((o) => o.date)
    .filter((d): d is string => Boolean(d))
    .sort();
  const when =
    dates.length === 0
      ? 'Any time'
      : fmtDay(parseMp(dates[0])) === fmtDay(parseMp(dates[dates.length - 1]))
        ? fmtDay(parseMp(dates[0]))
        : `${fmtDay(parseMp(dates[0]))} to ${fmtDay(parseMp(dates[dates.length - 1]))}`;
  const places = new Set(offers.map((o) => `${o.congregationId}:${o.externalUrl ?? ''}`)).size;

  return (
    <CampusSvgProvider value={svgMap}>
      <div ref={rootRef} className="sf flex flex-col gap-6 text-[#1c2b39]" style={vars}>
        <PageBand
          art={<BandArt imageUrl={type?.imageUrl ?? offers[0]?.imageUrl} />}
          title={type?.name ?? 'Care'}
          facts={[
            ...(type?.description
              ? [
                  {
                    icon: <Heart className="h-[18px] w-[18px]" />,
                    label: 'What',
                    value: (
                      <span className="line-clamp-3 font-medium">
                        {toPlainText(type.description)}
                      </span>
                    ),
                  },
                ]
              : []),
            { icon: <Clock className="h-[18px] w-[18px]" />, label: 'When', value: when },
            {
              icon: <MapPin className="h-[18px] w-[18px]" />,
              label: 'Where',
              value: `${places} ${places === 1 ? 'place' : 'places'}`,
              sub: offers.some((o) => o.isDelivery) ? 'Some deliver' : undefined,
            },
          ]}
        />
        {helpUrl && (
          <a
            href={helpUrl}
            className="inline-flex w-fit items-center gap-1 self-center rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-bold text-gray-600 no-underline shadow-sm transition-all hover:scale-[1.03] hover:text-[#1c2b39]"
          >
            Want to help instead?
            <ChevronRight className="h-4 w-4" strokeWidth={2.5} />
          </a>
        )}
        <CarePanel
          apiBaseUrl={apiBaseUrl}
          offers={offers}
          preselect={found.kind === 'offer' ? found.offer.id : null}
          accentStyle={vars}
        />
      </div>
    </CampusSvgProvider>
  );
}
